import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Zap, Search, MousePointerClick } from "lucide-react";
import { MaskedLine } from "@/components/site/Reveal";
import { scrollToAnchor } from "@/components/site/Header";
import { PROJECTS } from "@/data/site";

const perks = [
  { icon: Sparkles, label: "Design 100 % sur mesure" },
  { icon: Zap, label: "Rapide & optimisé" },
  { icon: Search, label: "Pensé pour Google" },
];

const DOMAINS = {
  essence: "lessence-restaurant.fr",
  "villa-azur": "villa-azur-iledere.fr",
  "atelier-coiffure": "latelier-coiffure-paris.fr",
  "noire-vtc": "noire-chauffeur-prive.fr",
  "atelier-renovation": "atelier-renovation.fr",
  "aqua-pro-plombier": "aquapro-plomberie.fr",
};

const bySlug = (slug) => PROJECTS.find((p) => p.slug === slug);

const FloatSlot = ({ slug, onClick, delay, testid }) => {
  const project = bySlug(slug);
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 40, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.05 }}
      className="relative block w-full aspect-[16/11] rounded-xl overflow-hidden border border-white/15 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.7)] group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
      title={`Voir ${project.shortName} de plus près`}
      data-testid={testid}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.img
          key={slug}
          src={project.image}
          alt={project.alt}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
      </AnimatePresence>
      <span className="absolute inset-0 border border-transparent group-hover:border-cyan-400/40 rounded-xl transition-colors" />
    </motion.button>
  );
};

const Badge = ({ children, className, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`absolute glass rounded-full px-3.5 py-1.5 font-mono-label text-[10px] tracking-[0.15em] uppercase text-white/90 whitespace-nowrap ${className}`}
  >
    {children}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 40]);

  const [center, setCenter] = useState("aqua-pro-plombier");
  const [floats, setFloats] = useState(["essence", "villa-azur", "atelier-coiffure", "noire-vtc", "atelier-renovation"]);
  const centerProject = bySlug(center);

  const swap = (i) => {
    const next = floats[i];
    setFloats((f) => f.map((s, idx) => (idx === i ? center : s)));
    setCenter(next);
  };

  return (
    <section id="accueil" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      <div className="glow-orb w-[560px] h-[560px] bg-blue-600/20 -top-40 -left-40" />
      <div className="glow-orb w-[480px] h-[480px] bg-violet-600/15 top-1/3 right-0" />
      <div className="glow-orb w-[380px] h-[380px] bg-cyan-500/10 bottom-0 left-1/3" />
      <div
        className="absolute inset-0 opacity-[0.25]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, black, transparent)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1.12fr_1fr] gap-16 lg:gap-10 items-center w-full">
        <motion.div style={{ y: yText }}>
          <MaskedLine delay={0.1}>
            <p className="section-label mb-6">Création de sites internet sur mesure</p>
          </MaskedLine>

          <h1 className="font-display font-extrabold text-[2.5rem] leading-[1.06] sm:text-5xl lg:text-[3.1rem] xl:text-[3.7rem] tracking-tight lg:leading-[1.08]" data-testid="hero-title">
            <MaskedLine delay={0.25}>
              <span className="lg:whitespace-nowrap">Création de sites internet</span>
            </MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-gradient lg:whitespace-nowrap">qui se démarquent.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="text-[var(--text-secondary)] text-base md:text-lg mt-7 max-w-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            Votre activité mérite un site internet professionnel, sur mesure, rapide et pensé pour transformer vos
            visiteurs en clients. Aperçu gratuit, sans engagement.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-wrap items-center gap-4 mt-9"
          >
            <Link
              to="/demande"
              className="btn-gradient inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-semibold text-white"
              data-testid="hero-cta-apercu"
            >
              Obtenir mon aperçu gratuit <ArrowRight size={17} />
            </Link>
            <button
              onClick={() => scrollToAnchor("realisations")}
              className="glass inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium text-white/90 hover:bg-white/[0.07] transition-colors"
              data-testid="hero-cta-realisations"
            >
              Découvrir nos réalisations
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="flex flex-wrap gap-x-7 gap-y-3 mt-10"
          >
            {perks.map((p) => (
              <div key={p.label} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                <p.icon size={14} className="text-[var(--accent-cyan)]" />
                {p.label}
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden mt-12"
            data-testid="hero-visual-mobile"
          >
            <div className="rounded-2xl border border-white/15 bg-[#0b0c1c] p-1.5 shadow-[0_30px_70px_-20px_rgba(37,99,235,0.4)]">
              <div className="flex items-center gap-1.5 px-2.5 py-1.5">
                <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
                <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
                <span className="w-2 h-2 rounded-full bg-[#28c840]" />
                <span className="ml-2 flex-1 h-4 rounded bg-white/[0.05] font-mono-label text-[8px] text-white/40 flex items-center px-2">
                  {DOMAINS[center]}
                </span>
              </div>
              <div className="relative rounded-xl overflow-hidden aspect-[16/10]">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.img
                    key={center}
                    src={centerProject.image}
                    alt={centerProject.alt}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    data-testid="hero-mobile-laptop-image"
                  />
                </AnimatePresence>
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              {floats.map((slug, i) => (
                <button
                  key={slug}
                  type="button"
                  onClick={() => swap(i)}
                  className="relative flex-1 aspect-[16/11] rounded-lg overflow-hidden border border-white/15 focus:outline-none active:scale-95 transition-transform"
                  data-testid={`hero-mobile-thumb-${i}`}
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.img
                      key={slug}
                      src={bySlug(slug).image}
                      alt={bySlug(slug).alt}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35 }}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                    />
                  </AnimatePresence>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4">
              <span className="glass rounded-full px-3 py-1.5 font-mono-label text-[9px] tracking-[0.15em] uppercase text-white/80">
                6 modèles de site
              </span>
              <span className="glass rounded-full px-3 py-1.5 font-mono-label text-[9px] tracking-[0.15em] uppercase">
                <span className="text-gradient font-semibold">100 % sur mesure</span>
              </span>
            </div>
            <p className="flex items-center justify-center gap-1.5 mt-4 font-mono-label text-[9px] tracking-[0.18em] uppercase text-[var(--text-muted)]">
              <MousePointerClick size={11} className="text-[var(--accent-cyan)]" />
              Touchez une vignette pour la voir en grand
            </p>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yVisual }} className="relative h-[460px] sm:h-[520px] hidden md:block" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 mx-auto top-[10%] w-[88%] z-10"
          >
            <div className="rounded-2xl border border-white/15 bg-[#0b0c1c] p-2 shadow-[0_40px_100px_-20px_rgba(37,99,235,0.35)]">
              <div className="flex items-center gap-1.5 px-3 py-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-3 flex-1 h-5 rounded-md bg-white/[0.05] font-mono-label text-[9px] text-white/40 flex items-center px-2.5">
                  {DOMAINS[center]}
                </span>
              </div>
              <div className="relative rounded-xl overflow-hidden aspect-[16/10]">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.img
                    key={center}
                    src={centerProject.image}
                    alt={centerProject.alt}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    loading="eager"
                    data-testid="hero-laptop-image"
                  />
                </AnimatePresence>
              </div>
            </div>
            <div className="mx-auto h-3 w-[55%] rounded-b-2xl bg-gradient-to-b from-[#1a1b30] to-[#0b0c1c] border-x border-b border-white/10" />
          </motion.div>

          <div className="animate-floaty absolute -top-2 right-0 w-[32%] z-20">
            <FloatSlot slug={floats[0]} onClick={() => swap(0)} delay={0.7} testid="hero-float-0" />
          </div>
          <div className="animate-floaty-slow absolute top-[36%] -left-4 w-[31%] z-20">
            <FloatSlot slug={floats[1]} onClick={() => swap(1)} delay={0.85} testid="hero-float-1" />
          </div>
          <div className="animate-floaty absolute top-[56%] right-[2%] w-[28%] z-20" style={{ animationDelay: "1.5s" }}>
            <FloatSlot slug={floats[2]} onClick={() => swap(2)} delay={1} testid="hero-float-2" />
          </div>
          <div className="animate-floaty-slow absolute -top-3 left-[4%] w-[25%] z-20" style={{ animationDelay: "2.4s" }}>
            <FloatSlot slug={floats[3]} onClick={() => swap(3)} delay={1.1} testid="hero-float-3" />
          </div>
          <div className="animate-floaty absolute top-[73%] left-[26%] w-[26%] z-20" style={{ animationDelay: "3.2s" }}>
            <FloatSlot slug={floats[4]} onClick={() => swap(4)} delay={1.2} testid="hero-float-4" />
          </div>

          <Badge className="top-[20%] -left-3 z-30" delay={1.35}>Design sur mesure</Badge>
          <Badge className="top-[38%] -right-4 z-30" delay={1.45}>SEO optimisé</Badge>
          <Badge className="top-[86%] right-[8%] z-30" delay={1.55}>6 modèles de site</Badge>
          <Badge className="top-[64%] -left-5 z-30" delay={1.65}>
            <span className="text-gradient font-semibold">Aperçu gratuit</span>
          </Badge>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.9 }}
            className="absolute -bottom-9 inset-x-0 flex items-center justify-center gap-2 font-mono-label text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]"
            data-testid="hero-swap-hint"
          >
            <MousePointerClick size={12} className="text-[var(--accent-cyan)]" />
            Cliquez sur une vignette pour la voir de plus près
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
